<template>
  <div class="p-3 w-full flex flex-col gap-2 m-auto h-[700px]">
    <div class="w-full flex-auto">
      <ag-grid-vue
        theme="legacy"
        :immutableData="true"
        :getRowId="(params: GetRowIdParams) => params.data.ordinal"
        :rowData="subdivision.paydowns"
        :columnDefs="colDefs"
        :defaultColDef="defaultColDefs"
        :autoSizeStrategy="{ type: 'fitGridWidth' }"
        class="mx-4 py-3 h-full"
        :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
        rowSelection="single"
        :singleClickEdit="true"
        :stopEditingWhenCellsLoseFocus="true"
        :suppressDragLeaveHidesColumns="true"
        :pinned-bottom-row-data="[{}]"
        :isFullWidthRow="(params: IsFullWidthRowParams) => params.rowNode.rowPinned === 'bottom'"
        :fullWidthCellRenderer="CustomAddNewCellRenderer"
        :fullWidthCellRendererParams="{
          callback: () => tryAddNewPaydown(),
          label: 'paydown',
          display: true,
        }"
        @row-data-updated="resize"
        @grid-size-changed="resize"
        @cell-editing-stopped="updatePaydown"
        @grid-ready="(event: GridReadyEvent) => gridApi = event.api"
      >
      </ag-grid-vue>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import { currencyFormatter, formatCurrency, getCellEditableClass } from "@/services/Utility";
import { usePropsStore } from "@/stores/props";
import { type Subdivision, type SubdivisionPaydown } from "@/types/Subdivision";
import { computed, ref } from "vue";
import CustomAddNewCellRenderer from "../cell-renderers/CustomAddNewCellRenderer.vue";
import { warn } from "@/services/ToastService";
import CustomDeleteIconCellRenderer from "../cell-renderers/CustomDeleteIconCellRenderer.vue";
import CustomDateCellRenderer from "../cell-renderers/CustomDateCellRenderer.vue";
import type {
  EditableCallbackParams,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  IsFullWidthRowParams,
  RowDataUpdatedEvent,
  ValueGetterParams,
} from "ag-grid-community";

const subdivision = defineModel<Subdivision>("subdivision", { required: true });
const paydownsEdited = defineModel<boolean>("paydownsEdited", { required: true });

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const cellClass = (row: any): string => {
  if (row.colDef.editable && row.colDef.editable(row)) {
    return getCellEditableClass();
  }
  return "";
};

const defaultColDefs = {
  filter: true,
  floatingFilter: false,
  cellDataType: "text",
  enableCellChangeFlash: false,
  autoHeaderHeight: true,
  cellClass: (row: any) => cellClass(row),
};

const maxPaydownSequence = computed<number>(() => {
  if (subdivision.value.paydowns.length) {
    return Math.max(...subdivision.value.paydowns.map((paydown) => paydown.ordinal));
  }
  return 0;
});

const tryAddNewPaydown = (): void => {
  if (maxPaydownSequence.value !== 0) {
    const latestPaydown: SubdivisionPaydown = subdivision.value.paydowns.filter((paydown) => paydown.ordinal === maxPaydownSequence.value)[0];

    if (latestPaydown.amount === 0) {
      warn("Warning", "Before adding a new paydown, you must first input an Amount for the previous paydown");
      return;
    }
  }
  let amount = 0;
  subdivision.value.paydowns.forEach((paydown) => (amount += paydown.amount));

  let principalBalance = subdivision.value.totalPrincipalBalance;
  if (subdivision.value.paydowns.length > 0) {
    const paydown = subdivision.value.paydowns[subdivision.value.paydowns.length - 1];
    principalBalance = paydown.principalBalance - paydown.amount;
  }

  let paydown: SubdivisionPaydown = {
    ordinal: subdivision.value.paydowns.length + 1,
    dateStamp: new Date(),
    principalBalance: principalBalance,
    amount: subdivision.value.totalPrincipalBalance - amount,
  };
  subdivision.value.paydowns = [paydown, ...subdivision.value.paydowns];

  paydownsEdited.value = true;
};

const deleteMostRecentPaydown = (): void => {
  if (subdivision.value.paydowns.length == 0) return;

  subdivision.value.paydowns = subdivision.value.paydowns.filter((paydown) => paydown.ordinal !== subdivision.value.paydowns.length);
  paydownsEdited.value = true;
};

const updatePaydown = (rowNode: any): void => {
  paydownsEdited.value = true;
};

const dateChanged = (rowIndex: number, date: Date): void => {
  const rowData = gridApi.value?.getDisplayedRowAtIndex(rowIndex);
  if (rowData == null) return;
  rowData.data.dateStamp = date;
  updatePaydown(rowData);
};

const colDefs = ref([
  {
    width: 40,
    editable: false,
    cellClass: "text-center flex items-center justify-center",
    headerClass: "text-center",
    filter: false,
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: () => "Are you sure you want to delete the most recent paydown row?",
      deleteConfirmHeader: () => "Delete Paydown",
      callback: deleteMostRecentPaydown,
      checkDeletable: (data: SubdivisionPaydown) => data.ordinal === maxPaydownSequence.value,
    },
  },
  {
    field: "ordinal",
    headerName: "#",
    cellClass: "text-center flex items-center justify-center",
    headerClass: "text-center",
    width: 50,
  },
  {
    field: "dateStamp",
    headerName: "Date",
    width: 100,
    cellDataType: "date",
    cellRenderer: CustomDateCellRenderer,
    cellRendererParams: {
      setValue: dateChanged,
      disableInput: (data: SubdivisionPaydown) => data.ordinal !== maxPaydownSequence.value,
    },
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "principalBalance",
    headerName: "Principal Balance",
    headerClass: "grid-align-center",
    width: 100,
    valueFormatter: currencyFormatter,
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "amount",
    headerName: "Amount",
    headerClass: "grid-align-center",
    width: 100,
    valueFormatter: currencyFormatter,
    editable: (params: EditableCallbackParams) => params.data.ordinal === maxPaydownSequence.value,
  },
  {
    cellClass: "text-end",
    headerName: "Adj. Principal Balance",
    headerClass: "grid-align-center",
    width: 100,
    valueGetter: (params: ValueGetterParams) => formatCurrency(params.data.principalBalance - params.data.amount),
  },
]);
</script>
